"use client";
import React, { useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Merchant } from "@/dto/merchant";
import { OrderType } from "@/constants/enum";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { useTranslations } from "next-intl";

export const NotAcceptingOrdersBannerContent = ({
  merchant,
  message = null,
}: {
  merchant: Merchant;
  message?: string | null;
}) => {
  const t = useTranslations("NotAcceptingOrdersBanner");
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const orderType = searchParams?.get("order_type");
  const isDineIn = orderType?.toLowerCase() === OrderType.DINE_IN.toLowerCase();
  const isGiftCardPage: boolean = pathName?.includes("giftcard") || false;
  const isConfirmationPage: boolean =
    pathName?.includes("confirmation") || false;
  const isStandaloneGiftCard = merchant.isStandaloneGiftCardWebsite;

  const getText = (merchant: Merchant) => {
    const isShopPage: boolean = pathName?.includes("shop") || false;
    let text;

    if (
      (isShopPage && merchant.isService && merchant.isHybrid) ||
      merchant.isRestaurant ||
      merchant.isRetail
    ) {
      text = "orders";
    } else {
      text = "appointments";
    }

    return text;
  };

  const text = getText(merchant);

  const bannerContent = useMemo(() => {
    if (message) return message;

    if (merchant.isEssentialMerchant) return t("essentialContent");

    if (merchant.isRestaurant && isDineIn)
      return t("dineInRestaurant", { merchantName: merchant.name, text });

    if (!isDineIn && !merchant.isEssentialMerchant && !isGiftCardPage)
      return t("restaurant", { merchantName: merchant.name, text });

    if (isStandaloneGiftCard && isGiftCardPage) return t("standaloneGiftCard");

    return "";
  }, [
    message,
    merchant,
    isDineIn,
    isGiftCardPage,
    isStandaloneGiftCard,
    text,
    t,
  ]);

  if (
    (merchant?.isSellingGiftCards && isGiftCardPage) ||
    isConfirmationPage ||
    !bannerContent
  ) {
    return null;
  }

  return (
    <div
      data-testid={applyDataTestId(pom.banners.notAcceptingOrderBanner)}
      className="w-screen flex flex-row justify-center items-center bg-[#E6F3FF] shadow-md"
      role="alert"
    >
      <p className="text-center py-3 text-[15px]">{bannerContent}</p>
    </div>
  );
};
